import React, { PropsWithChildren, useMemo } from 'react'
import useMediaQuery from '../../lib/hooks/useMediaQuery'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { useTheme } from '../../lib/styling'
import { ThemeBreakpoints, BreakpointName } from '../helperTypes'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** Hides the given breakpoint(s). */
  on?: BreakpointName | BreakpointName[]
  /** Hides at and above the given breakpoint. */
  up?: BreakpointName
  /** Hides at and below the given breakpoint. */
  down?: BreakpointName
}>

type DeprecatedProps = {
  only?: BreakpointName | BreakpointName[]
}

export type HiddenProps = Props & DeprecatedProps & CommonProps

/**
 * return css media query for which child component should be visible
 * @param props only/up/down props
 */
const getMediaQuery = ({ on, up, down }: Props, breakpoints: ThemeBreakpoints): String | null => {
  if (on) {
    const orderedBPS: Array<BreakpointName> = Object.keys(breakpoints) as Array<BreakpointName>
    // return mediaqueries for all 'visible' breakpoints
    const hiddenBPS = Array.isArray(on) ? on : [on]
    const visibleBPS = orderedBPS.filter((bp) => hiddenBPS.indexOf(bp) < 0)
    return visibleBPS
      .map((bpName) => {
        const minBP: number = breakpoints[bpName]
        const maxBP: number = breakpoints[orderedBPS[orderedBPS.indexOf(bpName) + 1]]
        if (bpName === 'xxl') {
          return `(min-width: ${minBP}px)`
        } else if (bpName === 'xxs') {
          return `(max-width: ${maxBP - 1}px)`
        }
        return `(min-width: ${minBP}px) and (max-width: ${maxBP - 1}px)`
      })
      .join(',')
  } else if (up) {
    const bp: number = breakpoints[up]
    return `(max-width: ${bp - 1}px)`
  } else if (down) {
    const bp: number = breakpoints[down]
    return `(min-width: ${bp}px)`
  }
  return null
}

const Hidden = (props: HiddenProps) => {
  const { children, only, on, up, down, ...rest } = props
  useDeprecatedProps('Hidden', { only: 'on' }, props)
  const {
    responsive: { breakpoint },
  } = useTheme()
  const mqs = useMemo(() => getMediaQuery({ on: on || only, up, down }, breakpoint), [breakpoint, only, on, up, down])
  const matches = useMediaQuery(mqs)
  if (matches) {
    if (React.isValidElement(children)) {
      return React.cloneElement(children, rest)
    } else return <>{children}</>
  } else return null
}

export default Hidden
